<!-- Competitor Expiring Contracts Section -->
<template>
  <base-expiring-contracts
    section-id="competitor-expiring-contracts-section"
    base-class="competitor-expiring-contracts-section"
    title="Competitor Contracts Expiring Soon"
    subtitle="Showing up to 50 competitor-held contracts, prioritized by nearest expiry date across the next 12 months."
    header-icon="mdi-alert-circle"
    empty-message="No competitor contracts expiring soon found"
    action-button-text="Add to Pipeline"
    action-button-icon="mdi-bookmark-plus"
    action-event-name="add-to-pipeline"
    :contracts="contracts"
    :is-loading="isLoading"
    :section-style="sectionStyle"
    :is-visible="isVisible"
    :items-per-page="itemsPerPage"
    :show-story="true"
    :story-builder="buildContractStory"
    @add-to-pipeline="contract => $emit('add-to-pipeline', contract)"
  >
    <!-- Chips: cluster, timeframe -->
    <template #chips="{ contract, getTimeframeChip, setTimeframeFromContract }">
      <!-- Cluster chips (one per matched cluster) -->
      <v-chip
        v-for="(cluster, cIdx) in (contract.matched_clusters || [])"
        :key="clusterChipKey(contract, cluster, cIdx)"
        x-small
        class="contract-chip contract-chip-cluster"
        :style="clusterChipCursorStyle(cluster)"
        @click.stop="cluster.cluster_id != null && cluster.cluster_id !== '' && navigateToCluster(cluster.cluster_id)"
      >
        <v-icon small left>mdi-folder-multiple</v-icon>
        {{ cluster.cluster_name }}
      </v-chip>

      <v-chip
        v-if="contract.category"
        x-small
        class="contract-chip competitor-expiring-category-chip"
      >
        <v-icon
          x-small
          left
          class="competitor-expiring-category-chip-icon"
        >
          mdi-tag-outline
        </v-icon>
        {{ contract.category }}
      </v-chip>

      <!-- Timeframe chip: click filters by this timeframe -->
      <template v-if="getTimeframeChip && setTimeframeFromContract">
        <v-chip
          v-if="getTimeframeChip(contract)"
          x-small
          :class="`contract-chip ${getTimeframeChip(contract).class}`"
          style="cursor: pointer;"
          @click.stop.prevent="setTimeframeFromContract(contract)"
        >
          <v-icon x-small left class="contract-chip-icon">{{ getTimeframeChip(contract).icon }}</v-icon>
          {{ getTimeframeChip(contract).label }}
        </v-chip>
      </template>
    </template>

    <!-- Custom actions: Add to Pipeline -->
    <template #actions="{ contract }">
      <v-btn
        small
        outlined
        color="primary"
        class="competitor-expiring-contracts-section-action-btn-add text-capitalize body-2"
        :loading="contract.adding"
        :disabled="contract.adding"
        @click.stop.prevent="handleAddToPipeline(contract)"
      >
        <v-icon small left>mdi-bookmark-plus</v-icon>
        Add to Pipeline
      </v-btn>
    </template>
  </base-expiring-contracts>
</template>

<script>
import BaseExpiringContracts from './BaseExpiringContracts.vue';
import { buildBaseContractStories } from './BaseExpiringContracts.vue';

export default {
  name: 'CompetitorExpiringContractsSection',

  components: {
    BaseExpiringContracts
  },

  props: {
    contracts: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    sectionStyle: {
      type: String,
      default: ''
    },
    isVisible: {
      type: Boolean,
      default: true
    },
    itemsPerPage: {
      type: Number,
      default: 10
    }
  },

  methods: {
    handleAddToPipeline(contract) {
      this.$emit('add-to-pipeline', contract);
    },

    clusterChipKey(contract, cluster, cIdx) {
      const id = cluster.cluster_id != null ? cluster.cluster_id : 'x';
      const name = cluster.cluster_name || '';
      return `cluster-${contract.tender_id}-${cIdx}-${id}-${name}`;
    },

    clusterChipCursorStyle(cluster) {
      return cluster.cluster_id != null && cluster.cluster_id !== ''
        ? 'cursor: pointer;'
        : 'cursor: default;';
    },

    navigateToCluster(clusterId) {
      const route = this.$router.resolve(`/cluster/${clusterId}`);
      window.open(route.href, '_blank');
    },

    /**
     * Custom story builder for competitor contracts.
     * Extends the base story builder with competitor-specific information.
     */
    buildContractStory(contract, formatCurrency) {
      // Build base stories using the shared utility function
      const stories = buildBaseContractStories(contract, formatCurrency);

      return stories.length > 0 ? `This is a ${stories.join(', ')}.` : '';
    }
  }
};
</script>

<style scoped>
/* Category chip — same treatment as CompetitorActivitySection / saved searches */
.competitor-expiring-category-chip.v-chip {
  background-color: #112233 !important;
  color: #ffffff !important;
}

.competitor-expiring-category-chip .v-chip__content {
  color: #ffffff !important;
}

.competitor-expiring-category-chip-icon {
  color: #ffffff !important;
}
</style>

<!-- Base layout + chip variants from BaseExpiringContracts -->
